import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    ModalV2,
    ModalV2Title,
    ModalV2Content,
    ModalV2Footer,
    ButtonGroup,
    Button,
    InputControl,
} from '@si-armes/dsfr-react';
import usagerClientReducer from '../../../../store/usagerReducer';
import {
    API_GENERER_CODE_VALIDATION,
    API_VERIFIER_CODE_VALIDATION,
    CONTEXT_GENERER_CODE_VALIDATION,
    CONTEXT_VERIFIER_CODE_VALIDATION,
} from '../../../../constants/apiUrls.constants';
import { getTelFormatWithoutSpace } from '../../../../components/Commun/Form/maskFunctions';

const ModaleVerificationCode = ({
    getApi,
    postApi,
    reinitialiseApi,
    titre,
    fermerModale,
    modaleOuverte,
    submitValider,
    dataCompteUsager,
    dateGenerationCode,
    setDateGenerationCode,
    renvoyerCode,
    setRenvoyerCode,
    isLoadingVerificationCodeValidation,
    erreurVerificationCodeValidation,
    messageVerificationCodeValidation,
    apiConfig,
}) => {
    const dialog = useRef();
    const [code, setCode] = useState('');
    const [codeValide, setCodeValide] = useState(false);
    const [tempsRestant, setTempsRestant] = useState(null);

    useEffect(() => {
        if (dialog.current) {
            dialog.current.showModal();
        }
    }, [dialog, modaleOuverte]);

    useEffect(() => {
        if (!dateGenerationCode) {
            return undefined;
        }

        const dureeValidite = 60;
        const dateExpiration = new Date(dateGenerationCode.getTime() + dureeValidite * 1000);

        const majTemps = () => {
            const dateNow = new Date();
            const diff = Math.floor((dateExpiration - dateNow) / 1000);

            if (diff > 0) {
                const minutes = Math.floor(diff / 60)
                    .toString()
                    .padStart(2, '0');
                const secondes = (diff % 60).toString().padStart(2, '0');
                setTempsRestant(`Renvoyer un nouveau code dans : ${minutes}:${secondes}`);
                setRenvoyerCode(false);
            } else {
                setTempsRestant('');
                setRenvoyerCode(true);
            }
        };

        majTemps();

        const interval = setInterval(majTemps, 1000);

        return () => clearInterval(interval);
    }, [dateGenerationCode, setRenvoyerCode]);

    const renvoyerCodeValidation = () => {
        if (!renvoyerCode) return;

        const personne = apiConfig.dataProvider(dataCompteUsager);
        postApi(
            usagerClientReducer,
            API_GENERER_CODE_VALIDATION.replace('{uniquid}', personne.uuid),
            {},
            CONTEXT_GENERER_CODE_VALIDATION,
        );

        reinitialiseApi(CONTEXT_GENERER_CODE_VALIDATION);

        const dateNow = new Date();
        setDateGenerationCode(dateNow);
        setRenvoyerCode(false);
    };

    const validerModification = () => {
        if (code.length !== 8) {
            return;
        }
        const personne = apiConfig.dataProvider(dataCompteUsager);
        getApi(
            usagerClientReducer,
            API_VERIFIER_CODE_VALIDATION.replace('{uniquid}', personne.uuid).replace(
                '{code}',
                code,
            ),
            CONTEXT_VERIFIER_CODE_VALIDATION,
        );
    };

    useEffect(() => {
        setCodeValide(code.length === 8);
    }, [code]);

    useEffect(() => {
        if (!isLoadingVerificationCodeValidation && !erreurVerificationCodeValidation) {
            if (codeValide) {
                submitValider();
            }
        }
    }, [isLoadingVerificationCodeValidation, erreurVerificationCodeValidation]);

    return (
        <>
            {modaleOuverte ? (
                <ModalV2
                    id="verificationCode"
                    idModalTitle="verificationCode-title"
                    size="lg"
                    onClose={fermerModale}
                    ref={dialog}
                >
                    <ModalV2Content>
                        <ModalV2Title id="verificationCode-title">{titre}</ModalV2Title>

                        <p>
                            Un e-mail de confirmation vous a été envoyé. Veuillez saisir le code
                            qu'il contient pour valider la modification de vos informations
                            personnelles. Si vous n'avez pas accès à l'adresse e-mail actuellement
                            renseigné sur votre compte, veuillez contacter la préfecture.
                        </p>

                        <InputControl
                            name="codeValidation"
                            label="Saisissez le code de 8 chiffres"
                            id="codeValidation"
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value);
                            }}
                            placeholder="8 chiffres"
                            maxLength="8"
                            maskFonction={getTelFormatWithoutSpace}
                            state={erreurVerificationCodeValidation ? 'error' : null}
                            message={messageVerificationCodeValidation ?? ''}
                        />

                        <>
                            {tempsRestant && (
                                <p className="fr-text--sm fr-mb-0 fr-mt-3w">{tempsRestant}</p>
                            )}
                        </>
                    </ModalV2Content>

                    <ModalV2Footer>
                        <ButtonGroup align="right" inline>
                            <Button
                                name="envoyerCode"
                                type="button"
                                onClick={renvoyerCodeValidation}
                                niveau="secondaire"
                                disabled={!renvoyerCode || isLoadingVerificationCodeValidation}
                            >
                                Renvoyer le code
                            </Button>
                            <Button
                                name="annuler"
                                type="button"
                                onClick={fermerModale}
                                disabled={isLoadingVerificationCodeValidation}
                                niveau="secondaire"
                            >
                                Annuler
                            </Button>
                            <Button
                                name="valider"
                                variant="primary"
                                type="submit"
                                disabled={!codeValide || isLoadingVerificationCodeValidation}
                                onClick={validerModification}
                            >
                                Valider
                            </Button>
                        </ButtonGroup>
                    </ModalV2Footer>
                </ModalV2>
            ) : null}
        </>
    );
};

ModaleVerificationCode.propTypes = {
    postApi: PropTypes.func.isRequired,
    getApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    titre: PropTypes.string,
    fermerModale: PropTypes.func.isRequired,
    modaleOuverte: PropTypes.bool,
    submitValider: PropTypes.func.isRequired,
    dataCompteUsager: PropTypes.shape({
        personnePhysique: PropTypes.shape({
            uuid: PropTypes.string.isRequired,
        }),
        responsableLegal: PropTypes.shape({
            uuid: PropTypes.string.isRequired,
        }),
    }),
    dateGenerationCode: PropTypes.string,
    setDateGenerationCode: PropTypes.func.isRequired,
    renvoyerCode: PropTypes.bool,
    setRenvoyerCode: PropTypes.func.isRequired,
    isLoadingVerificationCodeValidation: PropTypes.bool,
    erreurVerificationCodeValidation: PropTypes.string,
    messageVerificationCodeValidation: PropTypes.string,
    apiConfig: PropTypes.shape({
        dataProvider: PropTypes.func.isRequired,
    }).isRequired,
};

ModaleVerificationCode.defaultProps = {
    modaleOuverte: false,
};

export default ModaleVerificationCode;
